import { ModeOfPlay, MpBattlePack, PtgBattlePack } from '../../models/IBattlePack.js';
import { ScourgeType, scourgeBattlePack } from '../../models/IScourge.js';
export var ValidatorCategory;
(function (ValidatorCategory) {
    ValidatorCategory["General"] = "General";
    ValidatorCategory["Folder"] = "Folder";
    ValidatorCategory["GrandAlliance"] = "Grand Alliance";
    ValidatorCategory["Ascension"] = "Ascension";
    ValidatorCategory["RavagedCoast"] = "Ravaged Coast";
    ValidatorCategory["BlightedWilds"] = "Blighted Wilds";
})(ValidatorCategory || (ValidatorCategory = {}));
export const validatorCategoryOrder = [
    ValidatorCategory.General,
    ValidatorCategory.Folder,
    ValidatorCategory.GrandAlliance,
    ValidatorCategory.Ascension,
    ValidatorCategory.RavagedCoast,
    ValidatorCategory.BlightedWilds,
];
export const ALLOW_SOG = {
    id: 'allowsog',
    name: 'Allow Scourge of Ghyran',
    default: false,
    description: `Allow Scourge of Ghyran units, formations, enhancements and lores (from ${MpBattlePack.Ghb2025}) in a list that isn't ${MpBattlePack.Ghb2025}.`,
    category: ValidatorCategory.General,
};
export const ALLOW_SOA = {
    id: 'allowsoa',
    name: 'Allow Scourge of Aqshy',
    default: false,
    description: `Allow Scourge of Aqshy units, formations, enhancements and lores (from ${MpBattlePack.Ghb2026}) in a list that isn't ${MpBattlePack.Ghb2026}.`,
    category: ValidatorCategory.General,
};
// The home-rule validator that allows a given scourge type outside its native pack.
export function allowScourgeValidatorId(scourge) {
    if (scourge === ScourgeType.SoG)
        return ALLOW_SOG.id;
    if (scourge === ScourgeType.SoA)
        return ALLOW_SOA.id;
    return undefined;
}
// Whether scourge content of the given type may be used in a list. Allowed when it
// is not scourge (None), when the scourge is native to the list's battlepack
// (SoG -> GHB 2025-26, SoA -> GHB 2026-27), or when the matching home rule is set.
export function isScourgeAllowed(scourge, battlePack, validators) {
    if (scourge === ScourgeType.None)
        return true;
    if (scourgeBattlePack(scourge) === battlePack)
        return true;
    const allowId = allowScourgeValidatorId(scourge);
    return allowId ? hasValidator(allowId, validators) : false;
}
export const ALLOW_NON_BP_RORS = {
    id: 'allownonbprors',
    name: `Allow Regiments of Renown`,
    default: false,
    description: `Allow Regiments of Renown not exclusive to this battlepack`,
    category: ValidatorCategory.General,
};
export const ALLOW_UNIQUE = {
    // specifically for ptg, it's suggested you don't use unique
    id: 'allowunique',
    name: 'Allow UNIQUE Units',
    default: false,
    description: 'Path to Glory rules suggest not using UNIQUE units in your roster. Enable this option to allow them.',
    category: ValidatorCategory.General,
};
export const ALLOW_LEGENDS = {
    id: 'allowlegends',
    name: `Allow Legends`,
    default: false,
    description: `Allow legends units.`,
    category: ValidatorCategory.General,
};
export const ALLOW_GA_PATHS = {
    id: 'allowgapaths',
    name: 'Allow Grand Alliance Paths',
    default: false,
    description: 'Allow allied units to take paths from their own faction.',
    category: ValidatorCategory.GrandAlliance,
};
export const ALLOW_GA_ENHANCEMENTS = {
    id: 'allowgaenhancements',
    name: 'Allow Grand Alliance Enhancements',
    default: false,
    description: 'Allow allied units to take enhancements from their own faction.',
    category: ValidatorCategory.GrandAlliance,
};
export const ALLOW_GA_AOA = {
    id: 'allowgaaoa',
    name: 'Allow Grand Alliance Anvil of Apotheosis',
    default: false,
    description: 'Allow allied Anvil of Apotheosis units.',
    category: ValidatorCategory.GrandAlliance,
};
export const ALLOW_BW_CONCOCTIONS = {
    id: 'allowbwconcoctions',
    name: `Allow Ghyranite Concoctions`,
    default: true,
    description: `Allow Ghyranite Concoctions from White Dwarf.`,
    category: ValidatorCategory.BlightedWilds,
};
export const ALLOW_ASC_PATHS = {
    id: 'allowascpaths',
    name: `Allow Ascension Paths`,
    default: false,
    description: 'Enable selection of Battlepack: Ascension paths.',
    battlepack: PtgBattlePack.Ascension,
    category: ValidatorCategory.Ascension,
};
export const ALLOW_RC_ENHANCEMENTS = {
    id: 'allowrcenhancements',
    name: `Allow Ravaged Coast Enhancements`,
    default: false,
    description: 'Enable selection of Battlepack: Ravaged Coast enhancements.',
    battlepack: PtgBattlePack.RavagedCoast,
    category: ValidatorCategory.RavagedCoast,
};
export const ALLOW_RC_LORES = {
    id: 'allowrclores',
    name: `Allow Ravaged Coast Lores`,
    default: false,
    description: 'Enable selection of Battlepack: Ravaged Coast lores.',
    battlepack: PtgBattlePack.RavagedCoast,
    category: ValidatorCategory.RavagedCoast,
};
export const ALLOW_BW_ENHANCEMENTS = {
    id: 'allowbwenhancements',
    name: `Allow Blighted Wilds Enhancements`,
    default: false,
    description: 'Enable selection of Battlepack: Blighted Wilds enhancements.',
    battlepack: PtgBattlePack.BlightedWilds,
    category: ValidatorCategory.BlightedWilds,
};
export const ALLOW_BW_LORES = {
    id: 'allowbwlores',
    name: `Allow Blighted Wilds Lores`,
    default: false,
    description: 'Enable selection of Battlepack: Blighted Wilds lores.',
    battlepack: PtgBattlePack.BlightedWilds,
    category: ValidatorCategory.BlightedWilds,
};
export const ALLOW_BW_PATHS = {
    id: 'allowbwpaths',
    name: `Allow Blighted Wilds Paths`,
    default: false,
    description: 'Enable selection of Battlepack: Blighted Wilds paths.',
    battlepack: PtgBattlePack.BlightedWilds,
    category: ValidatorCategory.BlightedWilds,
};
export const ptgAllowEnhancementsValidators = [ALLOW_RC_ENHANCEMENTS, ALLOW_BW_ENHANCEMENTS];
export const ptgAllowLoresValidators = [ALLOW_RC_LORES, ALLOW_BW_LORES];
export const ptgAllowPathsValidators = [ALLOW_ASC_PATHS, ALLOW_BW_PATHS];
export const VALIDATE_HIGHLANDER = {
    id: 'validatehighlander',
    name: `Highlander Validation`,
    default: false,
    description: `Enable Highlander validation rules (only one of each unit allowed).`,
    category: ValidatorCategory.General,
};
export const ptgValidators = [
    ALLOW_UNIQUE,
    // Neither scourge is native to a Path to Glory battlepack, so both are offered.
    ALLOW_SOG,
    ALLOW_SOA,
    ALLOW_GA_AOA,
    ALLOW_GA_PATHS,
    ALLOW_GA_ENHANCEMENTS,
];
export const rcValidators = [
    ...ptgValidators,
    ALLOW_ASC_PATHS,
    ALLOW_BW_PATHS,
    ALLOW_BW_ENHANCEMENTS,
    ALLOW_BW_LORES,
];
export const bwValidators = [
    ...ptgValidators,
    ALLOW_BW_CONCOCTIONS,
    ALLOW_RC_ENHANCEMENTS,
    ALLOW_RC_LORES,
];
export const mpValidators = [ALLOW_LEGENDS, VALIDATE_HIGHLANDER];
// Matched-play home rules, including the cross-pack scourge toggle for the OTHER
// pack's scourge (the native pack's scourge is allowed by default, so it needs no
// toggle): GHB 2026-27 lists can opt into Scourge of Ghyran, GHB 2025-26 (and any
// other matched-play pack) can opt into Scourge of Aqshy.
function matchedPlayValidators(battlePack) {
    const scourgeToggle = battlePack === MpBattlePack.Ghb2026 ? ALLOW_SOG : ALLOW_SOA;
    return [...mpValidators, scourgeToggle];
}
// Drives the per-list "Disable collection validation" toggle. Stored in the list's
// validators array (which already syncs) but intentionally kept out of the registry
// arrays so it never appears in the Home Rules list.
export const DISABLE_COLLECTION_VALIDATION = 'disablecollectionvalidation';
export function hasValidator(id, validators) {
    return validators.some((v) => v.toLowerCase() === id.toLowerCase());
}
export function getDefaultValidators(battlePack) {
    let validators = [];
    if (battlePack.modeOfPlay === ModeOfPlay.PathToGlory) {
        validators = ptgValidators;
        if (battlePack.battlePack === PtgBattlePack.BlightedWilds) {
            validators = bwValidators;
        }
        else if (battlePack.battlePack === PtgBattlePack.RavagedCoast) {
            validators = rcValidators;
        }
    }
    else {
        validators = matchedPlayValidators(battlePack.battlePack);
    }
    return validators.filter((v) => v.default).map((v) => v.id);
}
export function getAvailableValidators(battlePack) {
    if (battlePack.modeOfPlay === ModeOfPlay.PathToGlory) {
        if (battlePack.battlePack === PtgBattlePack.BlightedWilds) {
            return [...bwValidators];
        }
        else if (battlePack.battlePack === PtgBattlePack.RavagedCoast) {
            return [...rcValidators];
        }
        return [...ptgValidators];
    }
    return matchedPlayValidators(battlePack.battlePack);
}
